import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Flex } from 'reflexbox';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import {
  Paginator,
  RefreshIcon,
  RegularText,
  StyledText,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  TeamIcon
} from 'boss-ui';
import useScoringDashboardLoader from '../libs/score-dashboard-hook';
import { useDynamoPaginator, useIntervalScheduler } from '../libs/hooks-lib';
import { ONE_MINUTE_IN_MS } from '../constants';
import ScoreboardOptions from '../components/ScoreboardOptions';

const DEFAULT_PAGE_SIZE = 15;

const Container = styled(Flex)`
    background: ${(props) => props.theme.color.form.background} 0% 0% no-repeat padding-box;
    width: 948px;
    padding: ${(props) => props.theme.margin.m};
    flex-direction: column;
`;

const ScoreItem = ({ score = {}, idx, teamName }) => {
  const borderStyle = teamName === score.team.name ? '1px solid #66bb6a' : '';
  return (
    <TableRow border={borderStyle}>
      <TableData width="32px">
        <Flex px="6px" justifyContent="space-between">
          <RegularText light fontSize="14px">
            #
          </RegularText>
          <RegularText light fontSize="14px">
            {idx + 1}
          </RegularText>
        </Flex>
      </TableData>
      <TableData>
        <RegularText noWrap light fontSize="14px">
          {score.team.name}
        </RegularText>
      </TableData>
      <TableData>
        <Flex justifyContent="flex-end" mr="6px">
          <RegularText light fontSize="14px">
            {Math.round(score.total)}
          </RegularText>
        </Flex>
      </TableData>
    </TableRow>
  );
};

// TODO: Dedup duplicated libs functions between boss-admin and boss-client: useRefreshScore, onError
export default function AllTeamsScoreBoard({ eventId, useRefreshScore, user, api, onError, isAdmin = false }) {
  const [autoRefreshGraph, setAutoRefreshGraph] = useState(false);
  const [{ pages, currentPage }, paginate] = useDynamoPaginator();
  const teamName = user && user.userInTeam ? user.userInTeam.name : '';

  const [includeHiddenTeams, setIncludeHiddenTeams] = useState(false);

  const [pageRequested, setPageRequested] = useState(0);

  const SKELETON_STYLE = {
    COLOR: useTheme().color.skeleton.color,
    EFFECT: useTheme().color.skeleton.highlightColor,
  };

  const lastEvaluatedKey = useMemo(() => {
    if (pageRequested > 0) {
      return pageRequested > currentPage ? pages[currentPage].nextPagePointer : pages[currentPage].lastPagePointer;
    }
    return null;
  }, [pages, currentPage, pageRequested]);

  useEffect(() => {
    // Reset to first page when includeHiddenTeams changes because lastEvaluatedKey should no longer
    // be computed based on previous response
    setPageRequested(0);
  }, [includeHiddenTeams, setPageRequested]);

  const getEventDashboardArgs = useMemo(() => {
    return {
      eventId,
      apiPageSize: DEFAULT_PAGE_SIZE,
      displayPageSize: DEFAULT_PAGE_SIZE,
      lastEvaluatedKey,
      includeHiddenTeams
    };
  }, [eventId, includeHiddenTeams, lastEvaluatedKey]);

  const onDashboardDataResponse = useCallback((resp) => {
    paginate(resp.pagination, pageRequested);
  }, [pageRequested, paginate]);

  const {getDashboard, dashboardData, loadingScore} = useScoringDashboardLoader({
    api,
    onError,
    onResponse: onDashboardDataResponse,
  });
  const [refreshHover, setRefreshHover, getLastUpdatedLabel] = useRefreshScore(loadingScore);

  useEffect(() => {
    getDashboard(getEventDashboardArgs);
  }, [pageRequested, includeHiddenTeams]);

  useIntervalScheduler({
    callback: () => {
      getDashboard(getEventDashboardArgs);
    },
    enabled: autoRefreshGraph,
    intervalMs: ONE_MINUTE_IN_MS,
  })

  return (
    <Flex justifyContent="center" mt="50px">
      <Container>
        <Flex justifyContent="center" alignItems="center" width={1} mb="40px">
          <Flex mr="4px">
            <TeamIcon size={40} color="#2c4b2d" />
          </Flex>
          <StyledText size="24px" light>
            All teams score
          </StyledText>
        </Flex>
        <Flex mb="6px" justifyContent='space-between'>
          <Flex
            alignItems="center"
            justifyContent="flex-start"
            onClick={() => getDashboard(getEventDashboardArgs)}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setRefreshHover(true)}
            onMouseLeave={() => setRefreshHover(false)}
          >
            <RefreshIcon rotate={loadingScore} hover={refreshHover} />
            <RegularText fontSize="12px" marginLeft="3px">
              {refreshHover ? (
                <RegularText fontSize="12px" color="#66bb6a">
                  Refresh score
                </RegularText>
              ) : (
                getLastUpdatedLabel()
              )}
            </RegularText>
          </Flex>
          <ScoreboardOptions includeHiddenTeams={includeHiddenTeams}
                             setIncludeHiddenTeams={setIncludeHiddenTeams}
                             autoRefreshGraph={autoRefreshGraph}
                             setAutoRefreshGraph={setAutoRefreshGraph}
                             isAdmin={isAdmin} />
        </Flex>
        {loadingScore ? (
          <SkeletonTheme color={SKELETON_STYLE.COLOR} highlightColor={SKELETON_STYLE.EFFECT}>
            <Skeleton width="100%" height="30px" />
          </SkeletonTheme>
        ) : (
          <>
            <Table>
              <TableHead backgroundColor="#373737">
                <TableRow>
                  <TableHeader xpadding="6px" height="30px">
                    <RegularText light mediumWeight fontSize="14px">
                      Rank
                    </RegularText>
                  </TableHeader>
                  <TableHeader>
                    <RegularText light mediumWeight fontSize="14px">
                      Team
                    </RegularText>
                  </TableHeader>
                  <TableHeader justify="flex-end">
                    <RegularText light mediumWeight fontSize="14px" textAlign="right">
                      Score
                    </RegularText>
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.length ? (
                  dashboardData.map((score, idx) => (
                    <ScoreItem
                      score={score}
                      key={score.team.name}
                      idx={idx + currentPage * DEFAULT_PAGE_SIZE}
                      teamName={teamName}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableData colspan={3}>
                      <Flex width={1} justifyContent="center" p="12px">
                        <RegularText>There is no data yet...</RegularText>
                      </Flex>
                    </TableData>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Flex width={1} justifyContent="center" mt="16px">
              <Paginator
                isNext={pages[currentPage] && pages[currentPage].nextPagePointer}
                onNext={() => setPageRequested(currentPage + 1)}
                isPrev={currentPage > 0}
                onPrev={() => setPageRequested(currentPage - 1)}
                paginating={loadingScore}
              />
            </Flex>
          </>
        )}
      </Container>
    </Flex>
  );
}
